import { z } from "zod";

/**
 * "可以被自动导航到的公开页面"的**单一所有者**。
 *
 * 这套规则原来只在扩展侧的 `cross-site-safety.ts` 里，用于跨站执行。
 * 但 `open_url`（去一个未登记站点）同样需要它 —— 而规则一旦有两份就会漂移：
 * 一处放宽、另一处没放宽，就等于有一条链路能导航到本不该去的地方。
 * 所以规则搬到这里，扩展侧改为从这里取（见 `apps/extension/src/cross-site-safety.ts`）。
 *
 * 判定是**保守**的：https、无凭据、无端口、无 hash、非本机/内网/IP 字面量、
 * 无私人参数、无危险路径。DNS 解析仍由服务端负责（这里不做网络访问）。
 */
const privateParameter = /^(?:access.?token|refresh.?token|token|secret|password|passwd|api.?key|authorization|code|session|sessionid|sid|email|phone)$/i;
const dangerousPath = /(?:^|\/)(?:logout|signout|delete|checkout|payment|place[-_]?order)(?:\/|$)/i;

export function isPublicNavigationUrl(value?: string): boolean {
  try {
    const url = new URL(value || ""), host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" || url.username || url.password || url.port || url.hash || !host.includes(".")) return false;
    if (host.endsWith(".") || /(?:^|\.)(?:localhost|local|internal|lan|home|test|invalid)$/.test(host) || host.includes(":")) return false;
    // Reject IP literals altogether, including URL's canonicalized integer/octal forms.
    if (/^[\d.]+$/.test(host) || [...url.searchParams.keys()].some(key => privateParameter.test(key))) return false;
    return !dangerousPath.test(url.pathname);
  } catch { return false; }
}

export const PublicNavigationUrlSchema = z.string().max(2_048).refine(
  (value) => isPublicNavigationUrl(value),
  "只接受不含凭据、端口或私人参数的公开 HTTPS 页面地址",
);
